import React, { useEffect, useMemo, useState } from 'react';
import MaximizeIcon from '@mui/icons-material/OpenInFull';
import { IconButton, Tooltip } from '@material-ui/core';
import Draggable from 'react-draggable';
import { Provider } from 'react-redux';
import { Popper } from '@mui/material';
import ReactDom from 'react-dom';

import { UploadingStatus } from '../../../../store/sharedStore/sharedStoreSlice';
import { useAppSelector } from '../../../../store/hooks';
import styles from './SidebarModals.module.scss';
import { store } from '../../../../store/store';

type TMinimizedUploadModal = {
  open: boolean;
  setOpen: (bool: boolean) => void;
  setMaximize: () => void;
};

type TUploadingRatio = {
  all: number;
  completed: number;
};

enum TUploadingState {
  COMPLETE = 'Complete',
  TRANSCODING = 'Processing...',
  UPLOADING = 'Uploading',
  WAITING = 'Waiting',
  FAILED = 'Failed',
}

type TUploadingToolTipInfo = {
  title: string;
  state: TUploadingState | '';
  progress: string;
};

const MinimizedUploadModal: React.FC<TMinimizedUploadModal> = ({ open, setOpen, setMaximize }) => {
  const { uploadingContentState, uploadProgress } = useAppSelector((state) => state.sharedStore);
  const uploadContentState = useMemo(
    () => uploadingContentState.filter((item) => item?.uploadMode !== 'additional-angle'),
    [uploadingContentState],
  );
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingRatio, setUploadingRatio] = useState<TUploadingRatio>({
    all: 0,
    completed: 0,
  });
  const [uploadingInfo, setUploadingInfo] = useState<TUploadingToolTipInfo[]>([]);

  const switchModal = () => {
    setIsDragging(false);
    setOpen(false);
  };

  const maximizeModal = () => {
    switchModal();
    setMaximize();
  };

  useEffect(() => {
    if (uploadContentState.length) {
      const completedUploads = uploadContentState.filter((x) => [UploadingStatus.FAILED, UploadingStatus.FINISHED].includes(x?.status));
      setUploadingRatio({
        all: uploadContentState.length,
        completed: completedUploads.length,
      });

      const toolTipInfo = uploadContentState.map((content) => {
        if (content?.name && content?.status) {
          const info: TUploadingToolTipInfo = {
            title: content.name,
            state: '',
            progress: '',
          };
          switch (content.status) {
            case UploadingStatus.UPLOADING:
              info.state = TUploadingState.UPLOADING;
              break;
            case UploadingStatus.TRANSCODING:
              info.state = TUploadingState.TRANSCODING;
              break;
            case UploadingStatus.FINISHED:
              info.state = TUploadingState.COMPLETE;
              break;
            case UploadingStatus.ADDED:
              info.state = TUploadingState.WAITING;
              break;
            case UploadingStatus.FAILED:
              info.state = TUploadingState.FAILED;
              break;
            default:
              break;
          }

          return info;
        }
      });
      if (toolTipInfo) {
        // @ts-ignore
        setUploadingInfo(toolTipInfo);
      }
    }

    if (uploadContentState.length === 0) {
      setUploadingInfo([]);
      setUploadingRatio({
        all: 0,
        completed: 0,
      });
    }
  }, [uploadContentState]);

  const handleDrag = () => {
    setIsDragging(!isDragging);
  };

  const parseToolTipInfo = () => {
    if (uploadingInfo.length) {
      return uploadingInfo.map(
        (content, index) =>
          content?.title &&
          content?.state && (
            <div className={styles.toolTipContainer} key={index}>
              <div className={styles.tooltipTitle}>{content.title}</div>
              <div>
                {content.state}{' '}
                {content.state === TUploadingState.UPLOADING && uploadProgress && uploadProgress !== 100 ? `${uploadProgress}%` : ''}
              </div>
            </div>
          ),
      );
    }
    return '';
  };

  return ReactDom.createPortal(
    <Provider store={store}>
      <Draggable onStart={handleDrag} onStop={handleDrag} bounds="parent">
        <Popper
          open={open}
          className={styles.minimizedModal}
          disablePortal
          style={{
            top: 'unset',
            left: 'unset',
            borderBottom: '0.5px solid black',
          }}
        >
          <Tooltip
            classes={{
              tooltip: styles.customTooltip,
            }}
            title={isDragging ? '' : parseToolTipInfo()}
            placement="top-start"
          >
            <div className={styles.toolTipArea}>
              <span className={styles.message}>
                Uploaded &nbsp; {uploadingRatio.completed}/{uploadingRatio.all}
              </span>
            </div>
          </Tooltip>

          <Tooltip title={'expand'}>
            <MaximizeIcon onClick={maximizeModal} className={styles.iconButtonMaximize} />
          </Tooltip>
        </Popper>
      </Draggable>
    </Provider>,
    document.getElementById('root')!,
  );
};

export default MinimizedUploadModal;
